import dagre from 'dagre';
import type { Dimensions, XYPosition } from '@xyflow/react';

interface LayoutNode extends Dimensions {
  id: string;
}

interface LayoutEdge {
  id: string;
  source: string;
  target: string;
}

export interface EdgeRoute {
  points: XYPosition[];
  label: XYPosition;
}

const PORT_SPACING = 24;
const LANE_SPACING = 20;
const PORT_STUB = 24;
const CORNER_RADIUS = 6;

interface Port {
  point: XYPosition;
  laneX: number;
}

function routeThroughLanes(middle: XYPosition[], source: Port, target: Port) {
  const first = middle[0];
  const last = middle[middle.length - 1];
  const result = [
    source.point,
    { x: source.laneX, y: source.point.y },
    { x: source.laneX, y: first.y },
    first,
  ];
  middle.slice(1).forEach((to, index) => {
    const from = middle[index];
    if (from.x !== to.x && from.y !== to.y) {
      const x = (from.x + to.x) / 2;
      result.push({ x, y: from.y }, { x, y: to.y });
    }
    result.push(to);
  });
  result.push(
    { x: target.laneX, y: last.y },
    { x: target.laneX, y: target.point.y },
    target.point,
  );
  return result.filter(
    (point, index) =>
      !index ||
      point.x !== result[index - 1].x ||
      point.y !== result[index - 1].y,
  );
}

export function roundedEdgePath(
  points: XYPosition[],
  radius = CORNER_RADIUS,
): string {
  if (!points.length) return '';
  let path = `M${points[0].x},${points[0].y}`;
  points.slice(1, -1).forEach((point, index) => {
    const before = points[index];
    const after = points[index + 2];
    const incoming = Math.hypot(point.x - before.x, point.y - before.y);
    const outgoing = Math.hypot(after.x - point.x, after.y - point.y);
    const bend = Math.min(radius, incoming / 2, outgoing / 2);
    if (
      !bend ||
      (before.x === point.x && point.x === after.x) ||
      (before.y === point.y && point.y === after.y)
    ) {
      path += ` L${point.x},${point.y}`;
      return;
    }
    const start = {
      x: point.x - ((point.x - before.x) * bend) / incoming,
      y: point.y - ((point.y - before.y) * bend) / incoming,
    };
    const end = {
      x: point.x + ((after.x - point.x) * bend) / outgoing,
      y: point.y + ((after.y - point.y) * bend) / outgoing,
    };
    path += ` L${start.x},${start.y} Q${point.x},${point.y} ${end.x},${end.y}`;
  });
  const end = points[points.length - 1];
  return `${path} L${end.x},${end.y}`;
}

export function layoutJobChain(nodes: LayoutNode[], edges: LayoutEdge[]) {
  const incoming = new Map<string, LayoutEdge[]>();
  const outgoing = new Map<string, LayoutEdge[]>();
  edges.forEach((edge) => {
    incoming.set(edge.target, [...(incoming.get(edge.target) ?? []), edge]);
    outgoing.set(edge.source, [...(outgoing.get(edge.source) ?? []), edge]);
  });
  const degree = (id: string) =>
    Math.max(incoming.get(id)?.length ?? 0, outgoing.get(id)?.length ?? 0);
  const maximumDegree = Math.max(1, ...nodes.map(({ id }) => degree(id)));
  const minimumHeights = new Map(
    nodes.map(({ id }) => [id, Math.max(64, (degree(id) + 1) * PORT_SPACING)]),
  );
  const graph = new dagre.graphlib.Graph({ multigraph: true });
  graph.setGraph({
    rankdir: 'LR',
    nodesep: 48,
    // Reserve a separate vertical lane for each port on both sides of a rank gap.
    ranksep: Math.max(96, 80 + (maximumDegree - 1) * LANE_SPACING * 2),
    edgesep: 24,
    marginx: 24,
    marginy: 24,
  });
  graph.setDefaultEdgeLabel(() => ({}));
  nodes.forEach(({ id, width, height }) =>
    graph.setNode(id, {
      width,
      height: Math.max(height, minimumHeights.get(id)!),
    }),
  );
  edges.forEach(({ id, source, target }) =>
    graph.setEdge(source, target, { width: 24, height: 24, labelpos: 'c' }, id),
  );
  dagre.layout(graph);

  const sourcePorts = new Map<string, Port>();
  const targetPorts = new Map<string, Port>();
  const assignPorts = (
    groups: Map<string, LayoutEdge[]>,
    isIncoming: boolean,
  ) => {
    const ports = isIncoming ? targetPorts : sourcePorts;
    const laneY = ({ id, source, target }: LayoutEdge) => {
      const points: XYPosition[] = graph.edge({
        v: source,
        w: target,
        name: id,
      }).points;
      return points[isIncoming ? points.length - 2 : 1].y;
    };
    groups.forEach((connections, nodeId) => {
      const node = graph.node(nodeId);
      const neighbour = isIncoming ? 'source' : 'target';
      const ordered = [...connections].sort(
        (a, b) =>
          laneY(a) - laneY(b) ||
          graph.node(a[neighbour]).y - graph.node(b[neighbour]).y ||
          a.id.localeCompare(b.id),
      );
      const direction = isIncoming ? -1 : 1;
      const planned = ordered.map((edge, index) => {
        const point = {
          x: node.x + (direction * node.width) / 2,
          y:
            node.y -
            node.height / 2 +
            (node.height * (index + 1)) / (ordered.length + 1),
        };
        const routeY = laneY(edge);
        return {
          edge,
          point,
          top: Math.min(point.y, routeY),
          bottom: Math.max(point.y, routeY),
          order: routeY < point.y ? index : ordered.length - 1 - index,
        };
      });
      // Reuse the same bend column unless vertical runs (including rounded corners)
      // conflict. Route outer branches first to avoid crossing inner branches.
      const lanes: { top: number; bottom: number }[][] = [];
      planned
        .sort((a, b) => a.order - b.order)
        .forEach(({ edge, point, top, bottom }) => {
          let laneIndex = 0;
          if (bottom - top > 0.01) {
            laneIndex = lanes.findIndex((occupied) =>
              occupied.every(
                (run) =>
                  bottom + CORNER_RADIUS * 2 <= run.top ||
                  top >= run.bottom + CORNER_RADIUS * 2,
              ),
            );
            if (laneIndex === -1) {
              laneIndex = lanes.length;
              lanes.push([]);
            }
            lanes[laneIndex].push({ top, bottom });
          }
          ports.set(edge.id, {
            point,
            laneX: point.x + direction * (PORT_STUB + laneIndex * LANE_SPACING),
          });
        });
    });
  };
  assignPorts(incoming, true);
  assignPorts(outgoing, false);

  return {
    minimumHeights,
    sizes: new Map<string, Dimensions>(
      nodes.map(({ id }) => {
        const { width, height } = graph.node(id);
        return [id, { width, height }];
      }),
    ),
    bounds: {
      x: 0,
      y: 0,
      width: graph.graph().width,
      height: graph.graph().height,
    },
    positions: new Map<string, XYPosition>(
      nodes.map(({ id }) => {
        const node = graph.node(id);
        return [
          id,
          { x: node.x - node.width / 2, y: node.y - node.height / 2 },
        ];
      }),
    ),
    routes: new Map<string, EdgeRoute>(
      edges.map(({ id, source, target }) => {
        const edge = graph.edge({ v: source, w: target, name: id });
        return [
          id,
          {
            points: routeThroughLanes(
              edge.points.slice(1, -1),
              sourcePorts.get(id)!,
              targetPorts.get(id)!,
            ),
            label: { x: edge.x, y: edge.y },
          },
        ];
      }),
    ),
  };
}
